// eslint-disable no-commented-code/no-commented-code
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  ErrorMessage,
  PageTitle,
  Link,
  NotFound,
  SelectDropdown,
} from '@gsa/afp-component-library';
import { emDashUnicode } from '../../../utilities/constants';
import ContractDetails from '../components/contract-details';
import TableWrapper from '../components/table-wrapper';
import ToastMessage from '../../../components/Toast/toast';
import {
  getColumns,
  RowSubComponent,
  ITEM_PER_PAGE_OPTIONS,
} from '../contract-line/columns';
import {
  GET_CONTRACT_VERSION_LINES_BY_CRITERIA,
  GET_CONTRACT_VERSION_LINES_FOR_FILTERS,
} from './lines-verion-query';
import { GET_CONTRACT_HEADER_VERSION_WITH_HISTORY } from '../contract-header-history-version/contract-header-history-version.gql';
import ContractLineListingFilter, {
  DEFAULT_FILTERS,
} from '../contract-line/contract-line-listing-search';
import OverlaySpinner from '../../../components/overlay-spinner';
import { isContractSOP } from '../components/contract-helpers';
import { ContractLineListingBreadcrumbs } from './line-version-breadcrumbs';
import { CONTRACT_ACTIONS, ContractActions } from '../contract-actions';
import '../contract-line/contract-line-listing.scss';

const ContractLineVersionListing = () => {
  const { contractHeaderId, versionNumber: vnStr } = useParams();
  const versionNumber = +vnStr;
  const navigate = useNavigate();

  const [alert, setAlert] = useState(null);
  const [order, setOrder] = useState([['scheduleLine', 'ASC']]);
  const [criteria, setCriteria] = useState({ contractHeaderId, versionNumber });
  const [paginationState, setPaginationState] = useState({
    limit: ITEM_PER_PAGE_OPTIONS[0],
    offset: 0,
    currentPage: 1,
  });
  const { limit, offset, currentPage } = paginationState;

  const {
    loading: loadingLines,
    error: errorLines,
    data: dataLines,
  } = useQuery(GET_CONTRACT_VERSION_LINES_BY_CRITERIA, {
    fetchPolicy: 'network-only',
    variables: {
      criteria,
      limit,
      offset,
      order,
    },
  });

  const { data: dataLinesForFilters, loading: loadingLinesForFilters } =
    useQuery(GET_CONTRACT_VERSION_LINES_FOR_FILTERS, {
      fetchPolicy: 'network-only',
      skip: !contractHeaderId,
      variables: { contractHeaderId, versionNumber },
    });

  const {
    data: dataHeader,
    error: errorHeader,
    called,
    loading: loadingHeader,
  } = useQuery(GET_CONTRACT_HEADER_VERSION_WITH_HISTORY, {
    variables: { contractHeaderId, versionNumber },
  });

  const contractLinesForFilter =
    dataLinesForFilters?.getContractVersionLinesForFilters ?? [];
  const contractLineCount =
    dataLines?.getContractVersionLinesByCriteria?.count || 0;
  const contractData = dataHeader?.getContractHeaderVersionWithHistory || {};
  const contractInfo = contractData.contractHeader || {};
  const contract = contractData.contractHeaderHistory || {};

  const vendor = contract.vendor || {};
  const vendorId = vendor.id || '';

  const isSop = isContractSOP(contract.solicitation?.purchaseTypeCode);
  const programs = contract.solicitation?.programs || [];

  const yearOptions = (contractInfo.contractHeaderRenewals ?? []).map(
    (chr) => ({
      value: chr.contractHeaderId,
      label: `Contract year ${chr.renewalYear ?? chr.contractYear}`,
    }),
  );
  const onYearChange = ({ target: { value } }) => {
    navigate(`/catalog/contract/contract-header/${value}/lines`);
  };
  const versionOptions = [...(contractInfo.contractHeaderVersions ?? [])]
    .sort((a, b) => (a.versionNumber < b.versionNumber ? 1 : -1))
    .map((chv, i) => ({
      value: i === 0 ? '' : chv.versionNumber,
      label: `version ${chv.versionNumber}`,
    }));
  const onVersionChange = ({ target: { value } }) => {
    const historySegment = value ? `/history/${value}` : '';
    navigate(
      `/catalog/contract/contract-header/${contractHeaderId}${historySegment}/lines`,
    );
  };

  const [contractLinesData, setContractLinesData] = useState([]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    if (!dataLines?.getContractVersionLinesByCriteria) return;
    const fetchedLines = dataLines.getContractVersionLinesByCriteria.rows;

    if (!programs.length) {
      setContractLinesData(fetchedLines);
      return;
    }

    const programNamesWithIds = programs?.reduce((a, c) => {
      return {
        ...a,
        [c.program]: {
          stdIds: c.solicitationLines.map((item) =>
            String(item.standardItem.standardItemNumber),
          ),
          solLines: c.solicitationLines.map((item) => String(item.id)),
        },
      };
    }, {});

    const getProgramName = (programNames, { stdId, solId }) => {
      let result;
      // if (stdId && solId) {
      //   result = Object.entries(programNames)
      //     ?.map(([key, value]) => {
      //       if (
      //         value.stdIds?.includes(stdId) &&
      //         value.solLines?.includes(solId)
      //       ) {
      //         return key;
      //       }
      //       return null;
      //     })
      //     .filter((item) => item)[0];
      //   return result;
      // }
      if (solId) {
        result = Object.entries(programNames)
          ?.map(([key, value]) => {
            if (value.solLines?.includes(solId)) {
              return key;
            }
            return null;
          })
          .find(Boolean);
        return result;
      }
      // if (stdId) {
      //   result = Object.entries(programNames)
      //     ?.map(([key, value]) => {
      //       if (value.stdIds?.includes(stdId)) {
      //         return key;
      //       }
      //       return null;
      //     })
      //     .filter((item) => item)[0];
      //   return result;
      // }

      return result;
    };

    setContractLinesData(
      fetchedLines.map((line) => {
        return {
          ...line,
          programName: getProgramName(programNamesWithIds, {
            // stdId: line.standardItem.standardItemNumber,
            stdId: null,
            solId: String(line.solicitationLineId),
          }),
        };
      }),
    );
  }, [dataLines, programs]);

  const onApplyFilters = (isReset) => {
    const newCriteria = { contractHeaderId, versionNumber };
    if (isReset) {
      setCriteria(newCriteria);
      return;
    }
    // eslint-disable-next-line
    for (const key of Object.keys(filters)) {
      if (filters[key]) {
        if (['makeCode', 'chassisMakeCode', 'modelYear'].includes(key))
          newCriteria[key] = +filters[key];
        else newCriteria[key] = filters[key];
      }
    }
    setCriteria(newCriteria);
  };

  const columns = useMemo(() => getColumns({ isSop }), [isSop]);

  if (!called) return null;

  if (errorLines) {
    return (
      <div id="contract-line-listing">
        <ContractLineListingBreadcrumbs />
        <ErrorMessage>An error occurred: {errorLines.message}</ErrorMessage>
      </div>
    );
  }

  if (
    called &&
    !loadingHeader &&
    !dataHeader?.getContractHeaderVersionWithHistory
  ) {
    return (
      <div id="contract-line-listing">
        <ContractLineListingBreadcrumbs />
        <NotFound />
      </div>
    );
  }

  return (
    <div id="contract-line-listing">
      {/* <StandardItemBodyChassisPanel hasChassis /> */}
      <ContractLineListingBreadcrumbs />
      {(loadingHeader || loadingLines || loadingLinesForFilters) && (
        <OverlaySpinner />
      )}
      {errorLines && (
        <ErrorMessage>An error occurred: {errorLines.message}</ErrorMessage>
      )}
      {alert && (
        <ToastMessage
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
          closable
          className="margin-bottom-2"
        />
      )}
      {!loadingHeader &&
        !errorHeader &&
        contract &&
        !errorLines &&
        dataLines && (
          <>
            <div className="grid-row grid-gap">
              <div className="grid-col-8">
                <PageTitle
                  title={
                    <>
                      Contract uPIID:{' '}
                      {contract.contractUpiid ||
                        contract.solicitation?.solicitationNumber ||
                        emDashUnicode}
                    </>
                  }
                />
              </div>
              <div className="grid-col-4 display-flex flex-justify-end">
                <SelectDropdown
                  id="contract_year_sel"
                  aria-label="contract year"
                  onChange={onYearChange}
                  options={yearOptions}
                  value={contractHeaderId}
                  className="text-bold font-body-md"
                  style={{ width: '13rem' }}
                />
                <span className="padding-left-1" />
                <SelectDropdown
                  id="contract_version_sel"
                  aria-label="contract version"
                  onChange={onVersionChange}
                  options={versionOptions}
                  value={versionNumber}
                  className="text-bold font-body-md"
                  style={{ width: '8.5rem' }}
                />
              </div>
            </div>
            <div className="grid-row grid-gap margin-bottom-2">
              <div className="grid-col-10 margin-top-1">
                <span>Contractor: </span>
                <Link
                  href={`${window.AFP_CONFIG.appURLs.home}/vendor/details/${vendorId}`}
                >
                  {vendor.vendorName}
                </Link>
              </div>
              <div className="grid-col-2 display-flex flex-align-end flex-justify-end">
                <ContractActions
                  actions={[CONTRACT_ACTIONS.VIEW_CONTRACT_VERSION]}
                  contractHeaderId={contractHeaderId}
                  versionNumber={versionNumber}
                />
              </div>
            </div>
            <ContractDetails contract={contract} />
            <ContractLineListingFilter
              contractLines={contractLinesForFilter}
              filters={filters}
              setFilters={setFilters}
              onApplyFilters={onApplyFilters}
              isSop={!!isSop}
            />
            <TableWrapper
              tableProps={{
                data: contractLinesData,
                columns,
                onSort: setOrder,
                defaultSort: order,
                expandable: true,
                // selectable: canUpdateContract,
                // onRowSelect: handleRowSelect,
                renderRowSubComponent: (props) => (
                  <RowSubComponent {...props} isSop={isSop} />
                ),
              }}
              paginationProps={{
                itemsPerPageOptions: ITEM_PER_PAGE_OPTIONS,
                onPageChange: (newPage, itemsPerPage) => {
                  setPaginationState({
                    ...paginationState,
                    currentPage: newPage,
                    offset: (newPage - 1) * itemsPerPage,
                    limit: itemsPerPage,
                  });
                },
                variant: 'advanced',
                itemsPerPage: limit,
                currentPage,
                itemsCount: contractLineCount,
                isReset: false,
              }}
              emptyStateText="There are no contract lines associated with this contract."
            />
          </>
        )}
    </div>
  );
};

export default ContractLineVersionListing;
